var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge, EmptyState, FlexBox, SelectionList, Spinner, Text, Tooltip, } from '@dixa/lib-shared-habla';
import { formatInternational } from 'lib-shared-phone-numbers';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';
import { numbersApi } from 'apis/numbers';
import { PhoneNumberCountryFlag } from 'components/phoneNumberCountryFlag/phoneNumberCountryFlag';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { HookFormSelectionList } from '../hookFormSelectionList';
import styles from './hookFormPhoneNumberSelect.module.scss';
/**
 * Represents the React Hook Form variant of the {@link InputSelectionList}
 * component for selecting phone numbers.
 */
export const HookFormPhoneNumberSelect = (_a) => {
    var _b;
    var { children } = _a, props = __rest(_a, ["children"]);
    const _c = (_b = props.componentProps) !== null && _b !== void 0 ? _b : {}, { capability, showCountryFlag = false, showRecordingInfo = false } = _c, componentProps = __rest(_c, ["capability", "showCountryFlag", "showRecordingInfo"]);
    const { arg, asyncStatus, items: phoneNumbers, changeArg, hasNextPage, nextPage, } = usePaginatedEndpoint({
        endpoint: numbersApi.endpoints.readNumbers,
        initialArg: { capability, limit: 20 },
        itemsSelector: ({ data }) => data.numbers,
        infinite: true,
    });
    const [searchTerm, setSearchTerm] = useState('');
    useUpdateEffect(() => {
        changeArg({ searchTerm, offset: 0 }, { debounceTime: 300 });
    }, [changeArg, searchTerm]);
    const selectedValue = useWatch({
        control: props.control,
        name: props.name,
    });
    const renderRecordingBadge = useCallback((phoneNumber) => {
        if (!showRecordingInfo || !phoneNumber.record_outbound_calls)
            return null;
        return (_jsx(Tooltip, Object.assign({ content: `Outbound call recording is active on ${formatInternational(phoneNumber.e164_format)}`, dark: true }, { children: _jsx(Badge, Object.assign({ variant: "error", size: "small" }, { children: "REC" })) })));
    }, [showRecordingInfo]);
    const inputSelectionListProps = useMemo(() => {
        var _a;
        return (Object.assign({ placeholder: 'Select a phone number', prefix: showCountryFlag
                ? {
                    type: 'custom',
                    value: (_jsx(PhoneNumberCountryFlag, { phoneNumber: (_a = selectedValue === null || selectedValue === void 0 ? void 0 : selectedValue[0]) === null || _a === void 0 ? void 0 : _a.e164_format })),
                    noWrap: false,
                }
                : undefined, equalityFn: isEqual, selectionListProps: {
                className: styles.selectionList,
            }, filter: {
                value: searchTerm,
                onChange: (e) => setSearchTerm(e.target.value),
            }, getItemTitleByValue: (phoneNumber) => (_jsxs(FlexBox, Object.assign({ gap: "300", alignItems: "center" }, { children: [_jsx(Text.Paragraph, { children: `${formatInternational(phoneNumber.e164_format)} ${phoneNumber.name ? `(${phoneNumber.name})` : ''}` }), renderRecordingBadge(phoneNumber)] }))), onToggleDropdown: (isOpen) => !isOpen && setSearchTerm('') }, componentProps));
    }, [
        componentProps,
        searchTerm,
        selectedValue,
        showCountryFlag,
        renderRecordingBadge,
    ]);
    return (_jsxs(HookFormSelectionList, Object.assign({}, props, { componentProps: inputSelectionListProps }, { children: [phoneNumbers.map((phoneNumber) => (_jsxs(SelectionList.Item, Object.assign({ value: phoneNumber, isDisabled: capability === 'sms' &&
                    phoneNumber.sms_functionality_state === 'deactivated', noFixedSelectionStateSpace: showCountryFlag, contentClassName: styles.selectionListItemContent }, { children: [showCountryFlag && (_jsx(PhoneNumberCountryFlag, { phoneNumber: phoneNumber.e164_format })), _jsxs("div", Object.assign({ className: styles.selectionListItemText }, { children: [_jsx(Text.Paragraph, { children: formatInternational(phoneNumber.e164_format) }), _jsx(Text.Paragraph, Object.assign({ subdued: true }, { children: phoneNumber.name }))] })), renderRecordingBadge(phoneNumber)] }), phoneNumber.e164_format))), asyncStatus === 'pending' && (_jsx(FlexBox, Object.assign({ as: "li", padding: "300", justifyContent: "center" }, { children: _jsx(Spinner, { size: 20 }) }))), asyncStatus === 'fulfilled' && !phoneNumbers.length && (_jsx(FlexBox, Object.assign({ as: "li", padding: "300", justifyContent: "center" }, { children: _jsx(EmptyState, { title: (arg === null || arg === void 0 ? void 0 : arg.searchTerm)
                        ? 'No phone numbers found'
                        : 'No phone numbers available', description: (arg === null || arg === void 0 ? void 0 : arg.searchTerm)
                        ? 'Try changing your search criteria.'
                        : 'Your organization has no phone numbers available.' }) }))), asyncStatus === 'fulfilled' && hasNextPage && (_jsx(Waypoint, { onEnter: nextPage }))] })));
};
